import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import orderActions from '../Actions/orderActions';
import subscriptionActions from '../Actions/Voss/subscriptionActions';
import useUserAccess from './useUserAccess';
import { UserRoles } from '../Constants/userRoles';
import { VossIntermediaryStatus } from '../Constants/VOSS/vossIntermediaryStatus';

import { VossProductType } from '../Constants/VOSS/vossProductType';
import { VossProductGroupType } from '../Constants/VOSS/vossProductGroupType';
import { VossTimeLengthUnits } from '../Constants/VOSS/vossTimeLengthUnits';

const getPlansForTier = (offer, tierId) => {
  if (!offer?.chargePlans || !tierId) return [];
  const validPlanIds = offer.chargePlans.filter(cp => cp.tierId === tierId).map(cp => cp.planId);
  return offer.plans?.filter(p => validPlanIds.includes(p.id)) ?? [];
};

const resolvePlanId = (offer, tierId, preferredPlanId) => {
  const plans = getPlansForTier(offer, tierId);
  if (plans.find(p => p.id === preferredPlanId)) return preferredPlanId;
  const twelveMonth = plans.find(p => p.bindingPeriodCycle?.length?.unit === VossTimeLengthUnits.Month && p.bindingPeriodCycle?.length?.value === 12);
  return twelveMonth?.id ?? plans[0]?.id ?? null;
};

export default function useVossOrder() {
  const dispatch = useDispatch();
  const { hasReadWriteAccess } = useUserAccess();

  // --- Selectors ---
  const product = useSelector(state => state.OrderReducer.product);
  const customer = useSelector(state => state.CustomerReducer.customer);
  const editOrderline = useSelector(state => state.OrderReducer.editOrderline);
  const dryRunResult = useSelector(state => state.OrderReducer.vossDryRunResult);
  const vossPriceCalculation = useSelector(state => state.OrderReducer.vossPriceCalculation);
  const intermediaries = useSelector(state => state.CommonReducer.intermediaries) ?? [];

  const offer = product?.offer;
  const subscription = product?.subscription;
  const migrationSourceSubscription = product?.migrationSourceSubscription ?? null;
  const customerId = customer?.vossCustomer?.id;

  // --- State ---
  const [selections, setSelections] = useState(null);
  const [tierChangeLoading, setTierChangeLoading] = useState(false);
  const skipDryRun = useRef(false);

  // --- Derived ---
  const scheduledChange = dryRunResult?.scheduledChanges?.find(sc => sc.tier);
  const selectablePlans = getPlansForTier(offer, selections?.tierId);

  // --- Init ---
  useEffect(() => {
    if (!offer) return;

    if (editOrderline?.vossDryRunRequest) {
      setSelections(editOrderline.vossDryRunRequest);
    } else {
      setSelections({
        customerId,
        productOfferId: offer.id,
        subscriptionId: subscription?.id ?? migrationSourceSubscription?.id ?? null,
        tierId: subscription?.tier?.id ?? null,
        planId: resolvePlanId(offer, subscription?.tier?.id, subscription?.plan?.id),
        optionalProductGroupIds: subscription?.productGroups
          ?.filter(g => g.productGroupType === VossProductGroupType.Optional)
          ?.map(g => g.id) ?? null,
        licenses: null,
        // Agreements that can no longer be applied are not carried into the new configuration. isAvailable describes the
        // agreement itself - da.status stays Active even when the agreement has lost its published version.
        discountAgreements: subscription?.discounts
          ?.filter(da => da.isAvailable !== false)
          ?.map(da => ({
            discountAgreementId: da.discountAgreementId,
            months: null
          })) ?? [],
        intermediaryInvoicingId: subscription?.intermediary?.invoicingId ?? null,
        billingStartDate: null,
      });
    }

    if (hasReadWriteAccess(UserRoles.PartnerSales)) {
      dispatch(subscriptionActions.getIntermediaries(VossIntermediaryStatus.Active));
    }
  }, [offer, subscription, editOrderline, dispatch]);

  // --- Dry run ---
  useEffect(() => {
    if (!selections?.tierId || !selections?.planId) return;
    if (skipDryRun.current) {
      skipDryRun.current = false;
      return;
    }

    const tier = offer?.tiers?.find(t => t.id === selections.tierId);
    const activeProductIds = new Set(
      (tier?.productGroups ?? [])
        .filter(g => g.productGroupType === VossProductGroupType.Included
          || (selections.optionalProductGroupIds ?? []).includes(g.id))
        .flatMap(g => g.products)
        .filter(p => p.type === VossProductType.License)
        .map(p => p.id)
    );

    const cleanedSelections = {
      ...selections,
      licenses: selections.licenses?.filter(l => activeProductIds.has(l.productId)) ?? null
    };

    dispatch(orderActions.vossDryRun(cleanedSelections, (data) => {
      skipDryRun.current = true;
      setTierChangeLoading(false);
      const normalized = data.normalizedRequest;
      const subLicenses = subscription?.licenses ?? migrationSourceSubscription?.licenses ?? [];
      const withExistingQuantity = licenses => licenses.map(l => {
        const existing = subLicenses.find(sl => sl.productId === l.productId);
        return { ...l, existingQuantity: existing?.quantity ?? 0 };
      });

      // A deferred tier change returns the request's licenses untouched, so on the first dry run
      // there is nothing to stamp: the selectors would start at 0 with no lower threshold and
      // BuildSaveModel would bill every license the operator sets as new on top of the existing
      // ones. Seed from the subscription so the delta is measured against what the customer has.
      if (normalized.licenses) {
        normalized.licenses = withExistingQuantity(normalized.licenses);
      } else if (subLicenses.length > 0) {
        normalized.licenses = withExistingQuantity(subLicenses.map(sl => ({
          productId: sl.productId,
          quantity: sl.quantity,
          initialQuantity: sl.quantity
        })));
      }

      setSelections(normalized);
    }));
  }, [selections, dispatch]);

  // --- Actions ---
  const updateSelection = useCallback((key, value) => {
    setSelections(prev => ({ ...prev, [key]: value }));
  }, []);

  const setOptionalGroupIds = useCallback((updater) => {
    setSelections(prev => ({ ...prev, optionalProductGroupIds: updater(prev.optionalProductGroupIds ?? []) }));
  }, []);

  const toggleOptionalGroup = useCallback((groupId) => {
    setOptionalGroupIds(current =>
      current.includes(groupId)
        ? current.filter(id => id !== groupId)
        : [...current, groupId]
    );
  }, [setOptionalGroupIds]);

  const selectExclusiveGroup = useCallback((selectedId, exclusiveGroupIds) => {
    setOptionalGroupIds(current => {
      const filtered = current.filter(id => !exclusiveGroupIds.includes(id));
      return selectedId ? [...filtered, selectedId] : filtered;
    });
  }, [setOptionalGroupIds]);

  const selectTier = useCallback((tierId) => {
    setTierChangeLoading(true);
    setSelections(prev => ({
      ...prev,
      tierId,
      planId: resolvePlanId(offer, tierId, subscription?.plan?.id),
      optionalProductGroupIds: null,
      licenses: null
    }));
  }, [offer, subscription]);

  const updateLicense = useCallback((productId, quantity) => {
    setSelections(prev => {
      const licenses = prev.licenses ?? [];
      const exists = licenses.some(l => l.productId === productId);
      return {
        ...prev,
        licenses: exists
          ? licenses.map(l => (l.productId === productId ? { ...l, quantity } : l))
          : [...licenses, { productId, quantity, initialQuantity: quantity }],
      };
    });
  }, []);

  return {
    offer,
    subscription,
    dryRunResult,
    vossPriceCalculation,
    selections,
    scheduledChange,
    migrationSourceSubscription,
    intermediaries,
    selectablePlans,
    tierChangeLoading,
    updateSelection,
    toggleOptionalGroup,
    selectExclusiveGroup,
    updateLicense,
    selectTier,
    ready: !!offer && !!selections,
  };
}